import {ReviewSummary} from 'constants/SourceConstants';
import {QueryResultType, SkippableBenchlingQueryResult} from 'graphql-client/BenchlingQuery';

import {HomepageAssignedProcessTasksQuery} from '../__generated/HomepageAssignedProcessTasksQuery';
import {
  REVIEW_STATUS_LABEL_TO_SUMMARY_KEY,
  REVIEW_SUMMARY_COUNT_KEYS,
  TASK_STATUS_LABEL_TO_SUMMARY_KEY,
  TASK_STATUS_TYPE_TO_SUMMARY_KEY,
  TASKS_REVIEW_SUMMARY_COUNT_KEYS,
  TasksReviewSummaryCountKey,
} from './MyWorkConstants';

export type TasksReviewSummary = Record<TasksReviewSummaryCountKey, number> & {
  ids: Record<TasksReviewSummaryCountKey, Array<string>>;
};

function createEmptyTasksReviewSummary(): TasksReviewSummary {
  return {
    cancelled: 0,
    completed: 0,
    failed: 0,
    invalid: 0,
    inProgress: 0,
    pending: 0,
    skipped: 0,
    ids: {
      cancelled: [],
      completed: [],
      failed: [],
      invalid: [],
      inProgress: [],
      pending: [],
      skipped: [],
    },
  };
}

function getFilteredStatusCount<SummaryKey extends string>(
  summary: Record<SummaryKey, number> | null,
  selectedStatusLabels: Array<string>,
  labelToSummaryKey: Record<string, SummaryKey | undefined>
): number {
  if (summary == null || selectedStatusLabels.length === 0) {
    return 0;
  }
  return selectedStatusLabels.reduce((acc, statusLabel) => {
    const summaryKey = labelToSummaryKey[statusLabel];
    if (summaryKey == null) {
      return acc;
    }
    return acc + (summary[summaryKey] ?? 0);
  }, 0);
}

export function getSelectedReviewSummaryIds(
  summary: ReviewSummary | null,
  selectedStatusLabels: Array<string>
): Array<string> | null {
  // null until the entry/worksheet review summary pre-query has loaded.
  if (summary == null) {
    return null;
  }
  return selectedStatusLabels.flatMap((statusLabel) => {
    const summaryKey = REVIEW_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel] as keyof ReviewSummary['ids'];
    return summary.ids[summaryKey];
  });
}

/** Total assigned reviewable items across all statuses. */
export function getReviewSummaryTotalCount(summary: ReviewSummary | null): number {
  if (summary == null) {
    return 0;
  }
  return REVIEW_SUMMARY_COUNT_KEYS.reduce((total, key) => total + (summary[key] ?? 0), 0);
}

export function makeTasksReviewSummary(
  tasksQueryResult: SkippableBenchlingQueryResult<HomepageAssignedProcessTasksQuery>
): TasksReviewSummary | null {
  if (tasksQueryResult.type !== QueryResultType.SUCCESS) {
    return null;
  }
  const summary = createEmptyTasksReviewSummary();
  for (const curr of tasksQueryResult.data.assignedProcessTaskStatusSummaries) {
    const statusKey = TASK_STATUS_TYPE_TO_SUMMARY_KEY[curr.status.statusType];
    summary[statusKey] = curr.count;
    summary.ids[statusKey] = curr.taskIds;
  }
  return summary;
}

/** Total assigned process tasks across all statuses. */
export function getTasksReviewSummaryTotalCount(summary: TasksReviewSummary | null): number {
  if (summary == null) {
    return 0;
  }
  return TASKS_REVIEW_SUMMARY_COUNT_KEYS.reduce((total, key) => total + summary[key], 0);
}

export function getSelectedTaskIds(
  tasksReviewSummary: TasksReviewSummary | null,
  selectedStatusLabels: Array<string>
): Array<string> | null {
  // null until the assigned process task summary pre-query has loaded.
  if (tasksReviewSummary == null) {
    return null;
  }
  return selectedStatusLabels.flatMap(
    (statusLabel) => tasksReviewSummary.ids[TASK_STATUS_LABEL_TO_SUMMARY_KEY[statusLabel]]
  );
}

export function getCategoryCounts({
  entryReviewSummary,
  worksheetReviewSummary,
  tasksReviewSummary,
  selectedEntryStatuses,
  selectedWorksheetStatuses,
  selectedTaskStatuses,
  showEntriesCategory = true,
  showWorksheetsCategory = true,
  showTasksCategory = true,
}: {
  entryReviewSummary: ReviewSummary | null;
  worksheetReviewSummary: ReviewSummary | null;
  tasksReviewSummary: TasksReviewSummary | null;
  selectedEntryStatuses: Array<string>;
  selectedWorksheetStatuses: Array<string>;
  selectedTaskStatuses: Array<string>;
  showEntriesCategory?: boolean;
  showWorksheetsCategory?: boolean;
  showTasksCategory?: boolean;
}): {entriesCount: number; worksheetsCount: number; tasksCount: number} {
  return {
    entriesCount: showEntriesCategory
      ? getFilteredStatusCount(entryReviewSummary, selectedEntryStatuses, REVIEW_STATUS_LABEL_TO_SUMMARY_KEY)
      : 0,
    worksheetsCount: showWorksheetsCategory
      ? getFilteredStatusCount(
          worksheetReviewSummary,
          selectedWorksheetStatuses,
          REVIEW_STATUS_LABEL_TO_SUMMARY_KEY
        )
      : 0,
    tasksCount: showTasksCategory
      ? getFilteredStatusCount(tasksReviewSummary, selectedTaskStatuses, TASK_STATUS_LABEL_TO_SUMMARY_KEY)
      : 0,
  };
}
